import { isDefined } from "./helpers";

export enum Environments {
    LOCAL = "local",
    TEST = "test",
    ACPT = "acpt",
    PREP = "prep",
    PROD = "prod",
}

export async function addExternalScript(
    src: string,
    id: string,
): Promise<void> {
    return new Promise(resolve => {
        const existingScript = document.getElementById(id);
        if (existingScript) {
            return resolve();
        }

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.id = id;
        script.src = src;
        // @ts-ignore
        script.onload = resolve;
        document.body?.appendChild(script);
    });
}

export function attemptMethod<T = any>(func: () => T, fallback: T): T {
    try {
        return func();
    } catch (e) {
        return fallback;
    }
}

export const throttle = (fn: Function, ms = 300) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let lastInvocation: any;
    let lastInvocationArgs: any[];

    return function (this: any, ...args: any[]) {
        lastInvocation = this;
        lastInvocationArgs = args;

        if (timeout) return;

        timeout = setTimeout(() => {
            fn.apply(lastInvocation, lastInvocationArgs);
            timeout = undefined;
        }, ms);
    };
};

export const debounce = (fn: Function, ms = 300) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
};

export const debouncePromise = <TIn>(
    fn: (params: TIn) => Promise<TIn>,
    ms = 300,
) => {
    let timerId: ReturnType<typeof setTimeout>;

    return function debounced(args: TIn) {
        if (timerId) {
            clearTimeout(timerId);
        }

        return new Promise<TIn>(resolve => {
            timerId = setTimeout(() => resolve(fn(args)), ms);
        });
    };
};

export const isEqual = (a: any, b: any) =>
    JSON.stringify(a) === JSON.stringify(b);

export function once(fn: Function | null, context?: any) {
    let result: any;
    if (typeof fn !== "function") {
        throw new TypeError("Expected a function");
    }

    return function (...args: any) {
        if (fn) {
            // @ts-ignore
            result = fn.apply(context || this, args);
            fn = null;
        }

        return result;
    };
}

export const sortBy = (key: string, caseSensitive: boolean = true) => {
    const locale = window.configuration?.locale?.substring(0, 2) || "";
    const collator = new Intl.Collator(locale, {
        sensitivity: caseSensitive ? "variant" : "base",
    });
    return (a: any, b: any) => collator.compare(a[key], b[key]);
};

export const isSku = (possibleSku: string) =>
    !!possibleSku &&
    !!possibleSku.match(new RegExp(/^[0-9]+$/)) &&
    possibleSku.length > 3;

export const isEAN = (possibleEAN?: string) => {
    if (!possibleEAN) return false;
    let ean = possibleEAN.replace(/[^0-9]/g, "");
    if (ean.length < 12 || ean.length > 13) return false;
    if (ean.length !== 13) ean = ean.padStart(13, "0");
    const checkDigit = parseInt(ean[12]);
    const eanWithoutCheckDigit = ean.slice(0, 12);
    const eanWithoutCheckDigitArray = eanWithoutCheckDigit.split("");
    const sum = eanWithoutCheckDigitArray.reduce((acc, curr, index) => {
        if (index % 2 === 0) return acc + parseInt(curr);
        return acc + parseInt(curr) * 3;
    }, 0);
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return checkDigit === calculatedCheckDigit;
};

export function hasWindow() {
    return typeof window !== "undefined";
}

export function parseIntSafe(value: string | undefined): number | undefined;
export function parseIntSafe(
    value: string | undefined,
    defaultValue: number,
): number;
export function parseIntSafe(value: string | undefined, defaultValue?: number) {
    if (!isDefined(value)) return defaultValue;

    const parsed = parseInt(value);

    return isNaN(parsed) ? defaultValue : parsed;
}

export function isObject(value: any) {
    const type = typeof value;
    return value != null && (type === "object" || type === "function");
}

export function omit(obj: Record<string, any>, omitKeys: Array<string>) {
    const result: Record<string, any> = {};
    Object.keys(obj).forEach(key => {
        if (omitKeys.indexOf(key) === -1) {
            result[key] = obj[key];
        }
    });
    return result;
}

export function pick(obj: Record<string, any>, keys: Array<string>) {
    const pickKeys = Array.isArray(keys) ? keys : [keys];
    let length = pickKeys.length;
    let key;
    const result: Record<string, any> = {};

    while (length > 0) {
        length -= 1;
        key = pickKeys[length];
        result[key] = obj[key];
    }
    return result;
}

export const canUseDOM = !!(
    typeof window !== "undefined" &&
    window.document &&
    window.document.createElement
);
